/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import {ColDef, ColumnState} from 'ag-grid-community';
import {globalGroupIdSelector} from '@ion/applications/src/store/selectors/global-group';
import {translate} from '@ion/core/src/locale/locale';
import {
  ScheduledReport,
  ScheduledReports
} from '@ion/core/src/types/entities';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {isEmpty} from '@ion/core/src/utils/StringUtil';
import CloseButton from '@ion/dls/src/components/buttons/CloseButton';
import CTALink from '@ion/dls/src/components/buttons/CTALink';
import {IconButtonLabelPlacement} from '@ion/dls/src/components/buttons/IconButton';
import {RefreshGridButton} from '@ion/dls/src/components/buttons/RefreshGridButton';
import GridControlsBank from '@ion/dls/src/components/grid/GridControlsBank';
import Dropdown from '@ion/dls/src/components/menus/Dropdown';
import SearchField from '@ion/dls/src/components/search/SearchField';
import {getUniqItems} from '@ion/dls/src/editors/ListEditorUtils';
import {
  defaultDateFilterParams,
  defaultTextFilterParams
} from '@ion/dls/src/grid/ColumnDefinitions';
import {
  definitionsToState,
  resolveDefinitions
} from '@ion/dls/src/grid/ColumnResolver';
import IonGrid, {AgGridType, IonGridAPI} from '@ion/dls/src/grid/IonGrid';
import {SortModelType} from '@ion/dls/src/grid/IonGridUtils';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {PAGE_SIZES} from '@ion/dls/src/grid/settings';
import useColumnChooser from '@ion/dls/src/grid/useColumnChooser';
import useColumnState from '@ion/dls/src/grid/useColumnState';
import {useIsPhone} from '@ion/dls/src/hooks/useMedia';
import Render from '@ion/dls/src/render/Render';
import {toggleThemeStyle} from '@ion/dls/src/theme/helpers';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import withSignals from '../../components/hoc/withSignals';
import {dispatchGridColumnsChange} from '../../store/helpers';
import {ScheduledReportRowEditor} from './ScheduledReportRowEditor';
import {getSearchTypeOptions, SearchType} from './SearchTypes';
import {
  scheduledGridColumnState,
  scheduledReportsSearchInputValue,
  scheduledReportsSearchType,
  scheduledReportsSearchTypeInputValue,
  scheduledReportsSearchValue,
  setScheduledGridColumnState,
  setScheduledGridFilterModel,
  setScheduledReportsSearchInputValue,
  setScheduledReportsSearchType,
  setScheduledReportsSearchTypeInputValue,
  setScheduledReportsSearchValue
} from './store';
import useLoadScheduledReports from './useLoadScheduledReports';

/**
 * Props for the ScheduledReportsGrid component.
 */
interface ScheduledReportsGridProps {
  viewSubGroups?: boolean;
  rowMenusEnabled?: boolean;
  omitHeader?: boolean;
  omitHeaderCTAs?: boolean;
  includeGlobalSearch?: boolean;
  isSchedulerDirector?: boolean,
  isSchedulerFleetManager?: boolean,
  toggleViewSubGroups?: Function;
  gridPageSize: number;
}
const spaceRegex = /,|\s|\s,|,\s/g;
const useStyles = makeStyles((theme) => ({
  endAdornment : {
    display              : 'flex',
    padding              : '0 10px',
    cursor               : 'pointer',
    '& .MuiSvgIcon-root' : {
      fontSize : 16
    }
  },
  search : {
    width                          : '100%',
    alignItems                     : 'center',
    justifyContent                 : 'space-between',
    gap                            : '4px',
    display                        : 'flex',
    padding                        : toggleThemeStyle('0', '0px 16px'),
    marginBottom                   : '20px',
    flexDirection                  : 'row',
    [theme.breakpoints.down('sm')] : {
      width         : '100%',
      flexDirection : 'column',
      alignItems    : 'start'
    }
  },
  searchControls : {
    width                          : '536px',
    alignItems                     : 'center',
    display                        : 'flex',
    gap                            : '4px',
    flexDirection                  : 'row',
    [theme.breakpoints.down('sm')] : {
      width         : '100%',
      flexDirection : 'column',
      alignItems    : 'start'
    }
  },
  searchType : {
    width        : '264px',
    paddingRight : '20px !important'
  },
  searchTypeInput : {
    padding  : '5px 2em !important',
    fontSize : '14px'
  },
  searchTypeOption : {
    fontSize : '14px !important'
  },
  searchInput : toggleThemeStyle({
    fontSize : '14px !important'
  }, {
    color         : dlsColor('grey1'),
    fontSize      : '14px',
    fontFamily    : 'Inter',
    fontWeight    : 500,
    lineHeight    : '16px',
    letterSpacing : '0'
  }),
  actionButtons : {
    display    : 'flex',
    alignItems : 'right'
  },
  clearFilters : {
    ...dlsTypography('cta'),
    color         : dlsColor('caBlue'),
    cursor        : 'pointer',
    letterSpacing : '1px',
    fontSize      : '13px',
    padding       : '12px',
    display       : 'block',
    whiteSpace    : 'nowrap',
    '&&:hover'    : {
      textDecoration : 'underline'
    },
    [theme.breakpoints.down('sm')] : {
      paddingLeft : 0
    }
  },
  adminCtas : {
    display                  : 'flex',
    gap                      : toggleThemeStyle('0px', '8px'),
    height                   : toggleThemeStyle('auto', '44px'),
    justifyContent           : toggleThemeStyle('center', 'flex-end'),
    alignItems               : 'center',
    width                    : toggleThemeStyle('auto', '100%'),
    '& .MuiButton-startIcon' : {
      marginRight : toggleThemeStyle('8px', '4px')
    }
  }
}));

// name of the reducer
export const NAME = 'scheduledReports';

// grid column state
export const SET_GRID_COLUMNS = 'SET_SCHEDULED_REPORTS_GRID_COLUMNS';

/**
 * @constructScheduledReportsGridRows
 *
 * Converts a collection of users and converts them to a list of items specific
 * for display within the Admin Users Grid, while retaining all of the items existing
 * attributes,
 */
export const constructScheduledReportsGridRows = (
  reports: ScheduledReports
): GridResult[] =>
  reports.map((report: ScheduledReport) => ({
    ...report,
    group         : report?.accountName,
    reportName    : report?.name,
    dashboardName : report?.dashboard?.title,
    ownerEmail    : report?.owner?.email,
    lastRun       : formatDateTime(report?.lastExecution?.statusTimestamp),
    nextRun       : formatDateTime(report?.nextRun),
    enabledUI     : translate(`common:modularsearch.enabled.${report?.enabled ? 'Yes' : 'No'}`)
  })) as GridResult[];

const ScheduledReportsGrid = forwardRef<IonGridAPI, ScheduledReportsGridProps>(({
  toggleViewSubGroups,
  viewSubGroups = false,
  rowMenusEnabled = true,
  omitHeader = false,
  omitHeaderCTAs = false,
  isSchedulerDirector = false,
  isSchedulerFleetManager = false
}: ScheduledReportsGridProps, ref) => {
  const classes                               = useStyles();
  const [gridApi, setGridApi]                 = useState<AgGridType>(null);
  const columnState                           = scheduledGridColumnState.value;
  const ionGridRef                            = useRef<IonGridAPI>();
  const dispatch                              = useDispatch();
  const globalAccountId                       = useSelector(globalGroupIdSelector);
  const isPhone                               = useIsPhone();
  const [refreshRequired, setRefreshRequired] = useState(false);
  const searchType                  = scheduledReportsSearchTypeInputValue.value;
  const appliedSearchType           = scheduledReportsSearchType.value;
  const searchInputValue            = scheduledReportsSearchInputValue.value;
  const searchValue                 = scheduledReportsSearchValue.value;
  const {isCurrent: isCurrentTheme} = useThemeContext();

  const searchFieldStyles = useCallback(() => ({
    searchFieldRoot : {
      height : toggleThemeStyle('55px', '49px')
    },
    searchFieldInput : {
      fontSize : '16px'
    }
  }), []);

  const attributeMappings = {
    group         : 'accountName',
    reportName    : 'name',
    dashboardName : 'dashboard',
    ownerEmail    : 'owner',
    lastRun       : 'lastExecution',
    nextRun       : 'nextRun',
    enabled       : 'status'
  };

  const defaultSort: SortModelType = {
    colId : 'name',
    sort  : 'asc'
  };

  const {definitions, defaultDefinitions} = useColumnState({
    columnDefinitions : getColumnDefinitions(),
    columnState
  });

  const {
    showColumnChooser,
    columnDefinitions,
    onColumnChooserSave,
    toggleColumnChooser
  } = useColumnChooser(definitions);

  const onColumnSelectionsUpdate = function(columns: ColDef[]): void {
    onColumnChooserSave(columns);
    dispatch(
      dispatchGridColumnsChange(
        definitionsToState(columns),
        NAME,
        SET_GRID_COLUMNS
      )
    );
  };

  const onColumnStateUpdate = useCallback((state: ColumnState[]): void => {
    setScheduledGridColumnState?.(state);
  }, []);
  const onGridReady = useCallback((api) => {
    setGridApi(api);
  }, []);

  const onFilterChanged = useCallback(
    function() {
      const filterModel = gridApi?.getFilterModel();
      if (filterModel) {
        dispatch(setScheduledGridFilterModel(filterModel) as any);
      }
    },
    [gridApi]
  );

  const {getAllScheduledReports, refresh, onRefresh, filterModel} =
    useLoadScheduledReports({
      gridApi,
      globalAccountId,
      viewSubGroups,
      searchValue,
      searchType : appliedSearchType
    });

  const refreshScheduledReports = useCallback((shouldToggleRefreshState: boolean = true) => {
    onRefresh?.(shouldToggleRefreshState);
    ionGridRef.current?.refreshCurrentBlock?.();
  }, [onRefresh]);

  const refreshCurrentBlock = useCallback(() => {
    refreshScheduledReports?.(false);
  }, [refreshScheduledReports]);

  // deref ionGridRef.current at call time, not at useImperativeHandle-factory
  // time, so this always reaches the grid's latest gridApi/augmentItems ...
  const updateScheduledReportRow = useCallback((item: ScheduledReport, versionCheck = false) => {
    ionGridRef.current?.updateRowNodes?.([item], versionCheck);
  }, []);

  useImperativeHandle(ref, () => ({
    ...ionGridRef.current,
    onRefresh           : refreshScheduledReports,
    refreshCurrentBlock : refreshCurrentBlock,
    updateRowNode       : updateScheduledReportRow
  }), [refreshScheduledReports, updateScheduledReportRow]);

  const onSearchChange = useCallback(
    (e: ChangeEvent<{ name?: string; value: string }>) => {
      e.persist();
      setScheduledReportsSearchInputValue?.(e.target.value);
    },
    []
  );

  const isSearchValueString = useCallback((value) => {
    const searchTermNames = value
      .replace(spaceRegex, ' ')
      .replace(/ +/g, ' ')
      .split(' ');
    return searchTermNames.some((item) => isNaN(item));
  }, []);

  const handleDeviceSearch = useCallback((e) => {
    if (e?.key !== 'Enter') {
      return;
    }
    setScheduledReportsSearchValue?.(searchInputValue);
    setScheduledReportsSearchType?.(searchType);
    setRefreshRequired(true);
  }, [searchInputValue, searchType]);

  const removeSearch = useCallback(() => {
    setScheduledReportsSearchInputValue?.('');
    setScheduledReportsSearchValue?.('');
    setRefreshRequired(true);
  }, []);

  // Selecting a search type only stages it: the pending input is cleared, while the
  // applied term and type stay untouched, so the grid keeps its current results and
  // no refetch is triggered. Both are committed together on Enter, in handleDeviceSearch.
  const handleSearchTypeChange = useCallback((e) => {
    setScheduledReportsSearchTypeInputValue(e.target.value as SearchType);
    setScheduledReportsSearchInputValue('');
  }, []);

  const searchTypeLabel = useMemo(
    () =>
      getSearchTypeOptions().find((type) => type.value === searchType)?.label,
    [searchType]
  );

  return (
    <>
      <Render.If condition={!isPhone}>
        <div className={classes.search}>
          <div className={classes.searchControls}>
            <div className={classes.searchType}>
              <Dropdown options={getSearchTypeOptions()}
                value={searchType}
                externalClasses={{
                  input  : classes.searchTypeInput,
                  option : classes.searchTypeOption
                }}
                labelAsValue={false}
                onChange={handleSearchTypeChange} />
            </div>
            <SearchField placeholder={translate(
              'sisense:analytics.modularSearch.placeholder',
              {
                type : searchTypeLabel
              }
            )}
            overrides={searchFieldStyles}
            onChange={onSearchChange}
            externalClasses={{
              input : classes.searchInput
            }}
            optionsProps={{
              detailedDeviceGridSearch : true,
              onKeyPress               : handleDeviceSearch,
              value                    : searchInputValue
            }}
            endAdornment={
              searchInputValue ? (
                <div className={classes.endAdornment} onClick={removeSearch}>
                  {isCurrentTheme ? <CloseButton /> : <CloseIcon />}
                </div>
              ) : null
            } />
          </div>
          <div className={classes.actionButtons}>
            {!!searchValue && (
              <CTALink className={classes.clearFilters} onClick={removeSearch}>
                {translate('sisense:analytics.modularSearch.clearFilters')}
              </CTALink>
            )}
            <GridControlsBank controlsClassName={classes.adminCtas}>
              <RefreshGridButton onRefresh={onRefresh}
                currentLabelPlacement={IconButtonLabelPlacement.HORIZONTAL} />
            </GridControlsBank>
          </div>
        </div>
      </Render.If>
      <IonGrid ref={ionGridRef}
        rowPerPageTextTransform='lowercase'
        paginationEnabled
        showCustomPaginationBar
        refreshRequired={refreshRequired || refresh}
        setRefreshRequired={setRefreshRequired}
        showHeaderCheckbox={false}
        checkboxesEnabled={false}
        loadItems={getAllScheduledReports}
        filterModel={filterModel}
        augmentItems={constructScheduledReportsGridRows}
        onFilterChanged={onFilterChanged}
        attributeMappings={attributeMappings}
        rowMenusEnabled={rowMenusEnabled}
        omitHeader={omitHeader}
        omitHeaderCTAs={omitHeaderCTAs}
        columnDefinitions={columnDefinitions}
        defaultDefinitions={defaultDefinitions}
        showColumnChooser={showColumnChooser}
        onColumnChooserSave={onColumnSelectionsUpdate}
        onColumnsUpdated={onColumnStateUpdate}
        onColumnChooserClose={function() {
          toggleColumnChooser(false);
        }}
        columnComponents={{
          rowMenu : item => (
            <ScheduledReportRowEditor data={item?.data}
              isSchedulerDirector={isSchedulerDirector}
              isSchedulerFleetManager={isSchedulerFleetManager}
              onRefresh={refreshCurrentBlock} />
          )
        }}
        pageSizes={PAGE_SIZES}
        defaultSort={defaultSort}
        onGridReady={onGridReady}
        hideSubGroups={!isPhone}
        viewSubGroups={viewSubGroups}
        toggleViewSubGroups={toggleViewSubGroups}
        usePreferedPageSize />
    </>
  );
});

/**
 * @getColumnDefinitionsDesktop
 *
 * Returns column definitions for the desktop grid implementation ...
 */
const getColumnDefinitionsDesktop = (): ColDef[] => {
  return [
    {
      headerName   : translate('sisense:analytics.group'),
      field        : 'group',
      sortable     : false,
      filter       : false,
      resizable    : true,
      unSortIcon   : false,
      hide         : false,
      filterParams : defaultTextFilterParams
    },
    {
      headerName   : translate('sisense:analytics.name'),
      field        : 'reportName',
      sortable     : false,
      filter       : false,
      resizable    : true,
      unSortIcon   : false,
      hide         : false,
      filterParams : defaultTextFilterParams
    },
    {
      headerName   : translate('sisense:analytics.dashboardName'),
      field        : 'dashboardName',
      sortable     : false,
      filter       : false,
      resizable    : true,
      unSortIcon   : false,
      hide         : false,
      filterParams : defaultTextFilterParams
    },
    {
      headerName   : translate('sisense:analytics.ownerEmail'),
      field        : 'ownerEmail',
      sortable     : false,
      filter       : false,
      resizable    : true,
      unSortIcon   : false,
      hide         : false,
      filterParams : defaultTextFilterParams
    },
    {
      headerName   : translate('sisense:analytics.lastRun'),
      field        : 'lastRun',
      sortable     : false,
      filter       : false,
      resizable    : true,
      unSortIcon   : false,
      hide         : false,
      filterParams : defaultDateFilterParams
    },
    {
      headerName   : translate('sisense:analytics.nextRun'),
      field        : 'nextRun',
      sortable     : false,
      filter       : false,
      resizable    : true,
      unSortIcon   : false,
      hide         : false,
      filterParams : defaultDateFilterParams
    },
    {
      headerName : translate('sisense:analytics.enabled'),
      field      : 'enabledUI',
      sortable   : false,
      filter     : false,
      resizable  : true,
      unSortIcon : false,
      hide       : false
    }
  ];
};

const getColumnDefinitions = () => ({
  desktop : resolveDefinitions(getColumnDefinitionsDesktop()),
  mobile  : resolveDefinitions(getColumnDefinitionsDesktop())
});

export default withSignals(ScheduledReportsGrid);
